import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useBlockNotifications } from "./Dashboard/hooks/useBlockNotifications";
import { toast } from "react-toastify";
import axios from "axios";
import BaseUrl from "../base-api";

const transformNotificationsToDepartments = (notifications) => {
    const categoryMap = new Map();

    notifications.forEach((notification) => {
        const category = notification.category || "";
        const title = notification.title_Hindi || notification.title;
        if (!title || !notification.subtitles) return;

        if (!categoryMap.has(category)) {
            categoryMap.set(category, new Map());
        }

        const subcategoryMap = categoryMap.get(category);
        if (!subcategoryMap.has(title)) {
            subcategoryMap.set(title, []);
        }

        notification.subtitles.forEach((sub) => {
            if (sub.subtitle_Hindi) {
                subcategoryMap.get(title).push({
                    name: sub.subtitle_Hindi,
                    id: sub.id,
                    hasFile: !!sub.file,
                    link: sub.link || null,
                });
            }
        });
    });

    return Array.from(categoryMap.entries()).map(
        ([category, subcategoryMap]) => ({
            category,
            subcategories: Array.from(subcategoryMap.entries()).map(
                ([title, subtitleArray]) => ({
                    name: title,
                    departments: subtitleArray,
                })
            ),
        })
    );
};

export default function Curriculum() {
    const { notifications, loading } = useBlockNotifications("Seminar");
    const [showSidebar, setShowSidebar] = useState(true);
    const [expandedSubcategory, setExpandedSubcategory] = useState(null);
    const [selectedDept, setSelectedDept] = useState("");
    const [content, setContent] = useState("कृपया किसी विभाग का चयन करें।");
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(false); // <-- Added loader state

    const dynamicDepartments = transformNotificationsToDepartments(notifications);

    const handleSubcategoryClick = (name) => {
        setExpandedSubcategory(expandedSubcategory === name ? null : name);
    };

    const handleItemClick = async (dept) => {
        setSelectedDept(dept.name);
        setContent(`यह ${dept.name} की जानकारी है।`);
        setShowSidebar(false);
        setLoadingPdf(true); // <-- Show loader

        try {
            const res = await axios.get(
                `${BaseUrl}/api/notificationsBlock/document/${dept.id}`,
                {
                    responseType: "blob",
                    timeout: 80000,
                }
            );

            const fileBlob = new Blob([res.data], { type: res.headers["content-type"] });
            const fileUrl = URL.createObjectURL(fileBlob);
            setPdfUrl(fileUrl);
        } catch (error) {
            console.error("Document fetch failed:", error);
            toast.error("फ़ाइल लोड नहीं हो सकी");
        } finally {
            setLoadingPdf(false); // <-- Hide loader
        }
    };

    return (
        <div className="d-flex flex-column flex-md-row vh-100">
            <button
                className="btn d-md-none m-2"
                onClick={() => setShowSidebar(!showSidebar)}
                style={{ borderColor: "#77236b", color: "#77236b" }}
            >
                ☰ पाठ्यक्रम मेनू
            </button>

            <aside
                className={`bg-white border-end p-3 shadow-sm col-md-3 overflow-auto ${showSidebar ? "d-block" : "d-none d-md-block"
                    }`}
                style={{ maxHeight: "100vh" }}
            >
                <h5 className="text-center fw-bold mb-4" style={{ color: "#77236b" }}>
                    📘 पाठ्यक्रम सूची
                </h5>
                {dynamicDepartments.map((cat, i) => (
                    <div key={i} className="mb-4">
                        <h6 className="fw-bold text-secondary">{cat.category}</h6>
                        {cat.subcategories.map((sub, j) => (
                            <div key={j} className="mb-2">
                                <button
                                    className="w-100 border-0 py-2 px-3 rounded text-white"
                                    onClick={() => handleSubcategoryClick(sub.name)}
                                    style={{ backgroundColor: "#77236b" }}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>{sub.name}</span>
                                        <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                                            {expandedSubcategory === sub.name ? "–" : "+"}
                                        </span>
                                    </div>
                                </button>
                                {expandedSubcategory === sub.name && (
                                    <ul className="list-group list-group-flush mt-2">
                                        {sub.departments.map((dept, k) => (
                                            <li
                                                key={k}
                                                className="list-group-item small rounded mb-1"
                                                onClick={() => handleItemClick(dept)}
                                                style={{
                                                    cursor: "pointer",
                                                    backgroundColor:
                                                        selectedDept === dept.name
                                                            ? "#77236b"
                                                            : "#f8f9fa",
                                                    color:
                                                        selectedDept === dept.name
                                                            ? "#fff"
                                                            : "#000",
                                                    border: "1px solid #e0dbe4",
                                                    transition: "0.2s ease",
                                                }}
                                            >
                                                {dept.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </aside>

            <main className="flex-grow-1 p-4 bg-white shadow-sm">
                <div className="card border-0 shadow-sm">
                    <div className="card-body">
                        <h5 className="card-title fw-bold mb-3" style={{ color: "#77236b" }}>
                            📄 पाठ्यक्रम विवरण
                        </h5>
                        <p className="card-text">{content}</p>

                        {/* Loader */}
                        {loadingPdf ? (
                            <div className="text-center mt-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">दस्तावेज़ लोड हो रहा है...</p>
                            </div>
                        ) : pdfUrl && (
                            <>
                                <hr />
                                <h6>📂 दस्तावेज़ सूची:</h6>
                                <table className="table table-bordered table-hover mt-3">
                                    <thead className="table-light">
                                        <tr>
                                            <th>क्रमांक</th>
                                            <th>दस्तावेज़ का नाम</th>
                                            <th>डाउनलोड / ओपन</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1</td>
                                            <td>{selectedDept}</td>
                                            <td>
                                                <a
                                                    href={pdfUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    download={`${selectedDept}.pdf`}
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    📥 डाउनलोड / खोलें
                                                </a>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
